import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, PageHeader, StatusPill, Empty, Stat, Btn, inputCls } from "../../components/dash/Kit";
import { api, errMessage } from "../../lib/api";

export function AdminBrandProfiles() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/brand-profiles").then((r) => setRows(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-brand-profiles-page">
      <PageHeader title="Brand Profiles" body="Profile completeness and key brand information per customer." />
      {rows.length === 0 ? (
        <Empty testId="admin-profiles-empty" title="No brand profiles yet" />
      ) : (
        <div className="space-y-3" data-testid="admin-profiles-list">
          {rows.map((p) => (
            <Card key={p.id} testId={`admin-profile-${p.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-display text-base font-semibold text-slate-900">{p.customer?.business_name}</p>
                  <p className="text-xs text-slate-500">{p.customer?.email}</p>
                  <p className="mt-2 text-sm text-slate-600">
                    {p.steps?.business?.industry || "Industry unknown"} ·{" "}
                    {(p.steps?.social?.platforms || []).join(", ") || "No platforms selected"}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {(p.completed_steps || []).length} of 11 steps saved
                  </p>
                </div>
                <StatusPill status={p.completed ? "Completed" : "Information Required"} />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminSubscriptions() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/subscriptions").then((r) => setRows(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-subscriptions-page">
      <PageHeader title="Subscriptions" body="Every subscription and its billing state." />
      {rows.length === 0 ? (
        <Empty testId="admin-subscriptions-empty" title="No subscriptions yet" />
      ) : (
        <Card testId="admin-subscriptions-table" className="overflow-x-auto p-0">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400">
              <tr>
                {["Customer", "Package", "Price", "Next billing", "Revisions used", "Status"].map((h) => (
                  <th key={h} className="px-5 py-3 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((s) => (
                <tr key={s.id} data-testid={`admin-subscription-row-${s.id}`}>
                  <td className="px-5 py-3 font-semibold text-slate-900">{s.customer?.business_name || "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{s.package}</td>
                  <td className="px-5 py-3 text-slate-600">€{s.price}</td>
                  <td className="px-5 py-3 text-slate-600">{s.next_billing_date}</td>
                  <td className="px-5 py-3 text-slate-600">{s.revisions_used || 0}</td>
                  <td className="px-5 py-3"><StatusPill status={s.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

export function AdminInvoices() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/invoices").then((r) => setRows(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-invoices-page">
      <PageHeader
        title="Invoices"
        body="Invoices appear here once a payment provider is connected and billing runs are activated."
      />
      {rows.length === 0 ? (
        <Empty testId="admin-invoices-empty" title="No invoices yet" body="Nothing has been billed through the platform yet." />
      ) : (
        <div className="space-y-3">
          {rows.map((i) => (
            <Card key={i.id} testId={`admin-invoice-${i.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-800">
                  {i.invoice_number} · {i.customer?.business_name} · €{i.amount}
                </span>
                <StatusPill status={i.status} />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminSupport() {
  const [rows, setRows] = useState([]);
  const [macros, setMacros] = useState([]);
  const [filter, setFilter] = useState("open");
  const [replies, setReplies] = useState({});
  const [busy, setBusy] = useState("");
  const [drafting, setDrafting] = useState("");
  const [draftNote, setDraftNote] = useState({});

  const load = () => api.get("/admin/support").then((r) => setRows(r.data)).catch(() => {});
  const loadMacros = () => api.get("/admin/support/macros").then((r) => setMacros(r.data)).catch(() => {});
  useEffect(() => {
    load();
    loadMacros();
  }, []);

  const applyMacro = async (ticketId, macroId) => {
    if (!macroId) return;
    try {
      const { data } = await api.post(`/admin/support/${ticketId}/macro/${macroId}`);
      setReplies((r) => ({ ...r, [ticketId]: data.body }));
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const draft = async (ticketId) => {
    setDrafting(ticketId);
    try {
      const { data } = await api.post(`/admin/support/${ticketId}/draft`);
      setReplies((r) => ({ ...r, [ticketId]: data.reply || "" }));
      setDraftNote((n) => ({
        ...n,
        [ticketId]: { confidence: data.confidence, needs_human: data.needs_human || [] },
      }));
      toast.success("Draft ready — check it before sending.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setDrafting("");
    }
  };

  const send = async (id) => {
    const message = (replies[id] || "").trim();
    if (!message) {
      toast.error("Write a reply first.");
      return;
    }
    setBusy(id);
    try {
      await api.post(`/admin/support/${id}/reply`, { message });
      setReplies((r) => ({ ...r, [id]: "" }));
      setDraftNote((n) => ({ ...n, [id]: null }));
      toast.success("Reply sent — the customer is notified by email too.");
      load();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy("");
    }
  };

  const setStatus = async (id, status) => {
    setBusy(id);
    try {
      await api.patch(`/admin/support/${id}`, { status });
      load();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy("");
    }
  };

  const visible = filter === "all" ? rows : rows.filter((t) => t.status === filter);
  const counts = {
    open: rows.filter((t) => t.status === "open").length,
    answered: rows.filter((t) => t.status === "answered").length,
    closed: rows.filter((t) => t.status === "closed").length,
  };

  return (
    <div className="space-y-6" data-testid="admin-support-page">
      <PageHeader
        title="Support"
        body="Messages from the website contact form and customer dashboards. Replying emails the customer and shows up in their dashboard."
      />

      <div className="flex flex-wrap gap-2" data-testid="admin-support-filters">
        {[
          ["open", `Needs a reply (${counts.open})`],
          ["answered", `Answered (${counts.answered})`],
          ["closed", `Closed (${counts.closed})`],
          ["all", `All (${rows.length})`],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            data-testid={`admin-support-filter-${key}`}
            className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
              filter === key
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <Empty testId="admin-support-empty" title="Nothing here" body="No messages with this status." />
      ) : (
        <div className="space-y-3" data-testid="admin-support-list">
          {visible.map((t) => {
            const thread = t.messages?.length
              ? t.messages
              : [{ id: "legacy", author: "customer", body: t.message, created_at: t.created_at }];
            return (
              <Card key={t.id} testId={`admin-ticket-${t.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {t.topic} · {t.business_name || t.name || "Website visitor"}
                      {t.priority === "Priority" && (
                        <span className="ml-2 rounded-full bg-[#FFC43D]/30 px-2 py-0.5 text-xs font-bold text-amber-900">
                          PRIORITY
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500">
                      {t.email} · {t.source} · last message{" "}
                      {new Date(t.last_message_at || t.created_at).toLocaleString()}
                    </p>
                  </div>
                  <StatusPill status={t.status} testId={`admin-ticket-status-${t.id}`} />
                </div>

                <ul className="mt-4 space-y-3" data-testid={`admin-ticket-thread-${t.id}`}>
                  {thread.map((m, i) => (
                    <li
                      key={m.id || i}
                      className={`max-w-2xl rounded-2xl p-3 text-sm ${
                        m.author === "postmint" ? "ml-auto bg-slate-900 text-white" : "bg-slate-100 text-slate-800"
                      }`}
                    >
                      <p
                        className={`text-xs font-semibold uppercase tracking-wide ${
                          m.author === "postmint" ? "text-slate-300" : "text-slate-500"
                        }`}
                      >
                        {m.author === "postmint" ? `PostMint · ${m.author_name || "team"}` : m.author_name || "Customer"}
                      </p>
                      <p className="mt-1 whitespace-pre-line">{m.body}</p>
                      <p className={`mt-1.5 text-xs ${m.author === "postmint" ? "text-slate-400" : "text-slate-400"}`}>
                        {m.created_at ? new Date(m.created_at).toLocaleString() : ""}
                      </p>
                    </li>
                  ))}
                </ul>

                <div className="mt-4 space-y-3">
                  {t.status === "closed" ? (
                    <p className="text-sm text-slate-500">
                      This conversation is closed. Reopen it to reply.
                    </p>
                  ) : (
                    <>
                      <div className="flex flex-wrap gap-2">
                        <select
                          defaultValue=""
                          onChange={(e) => {
                            applyMacro(t.id, e.target.value);
                            e.target.value = "";
                          }}
                          data-testid={`admin-macro-select-${t.id}`}
                          className={`${inputCls} sm:max-w-xs`}
                        >
                          <option value="">Insert a saved reply…</option>
                          {macros.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.title}
                            </option>
                          ))}
                        </select>
                        <Btn
                          variant="outline"
                          disabled={drafting === t.id}
                          onClick={() => draft(t.id)}
                          testId={`admin-draft-button-${t.id}`}
                        >
                          {drafting === t.id ? "Drafting…" : "Draft with AI"}
                        </Btn>
                      </div>

                      {draftNote[t.id] && (
                        <div
                          className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
                          data-testid={`admin-draft-note-${t.id}`}
                        >
                          AI draft · confidence: {draftNote[t.id].confidence || "unknown"}. Check it before sending.
                          {draftNote[t.id].needs_human?.length > 0 && (
                            <ul className="mt-1 list-inside list-disc">
                              {draftNote[t.id].needs_human.map((n) => {
                                const label = typeof n === "string" ? n : JSON.stringify(n);
                                return <li key={label}>{label}</li>;
                              })}
                            </ul>
                          )}
                        </div>
                      )}

                      <textarea
                        rows={5}
                        value={replies[t.id] || ""}
                        onChange={(e) => setReplies((r) => ({ ...r, [t.id]: e.target.value }))}
                        placeholder="Write your reply…"
                        data-testid={`admin-reply-input-${t.id}`}
                        className={inputCls}
                      />
                    </>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {t.status !== "closed" && (
                      <Btn onClick={() => send(t.id)} disabled={busy === t.id} testId={`admin-reply-button-${t.id}`}>
                        Send reply
                      </Btn>
                    )}
                    {t.status !== "closed" ? (
                      <Btn
                        variant="outline"
                        onClick={() => setStatus(t.id, "closed")}
                        disabled={busy === t.id}
                        testId={`admin-close-button-${t.id}`}
                      >
                        Close
                      </Btn>
                    ) : (
                      <Btn
                        variant="outline"
                        onClick={() => setStatus(t.id, "open")}
                        disabled={busy === t.id}
                        testId={`admin-reopen-button-${t.id}`}
                      >
                        Reopen
                      </Btn>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function AdminAnalytics() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics").then((r) => setStats(r.data)).catch(() => setStats(false));
  }, []);

  if (!stats) return <Card testId="admin-analytics-loading">Loading analytics…</Card>;

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      <PageHeader title="Analytics" body="All figures come directly from the database — nothing is estimated." />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat testId="analytics-active" label="Active Customers" value={stats.active_customers} />
        <Stat testId="analytics-mrr" label="MRR" value={`€${stats.mrr}`} hint="excl. VAT" />
        <Stat testId="analytics-new" label="New This Month" value={stats.new_customers_this_month} />
        <Stat testId="analytics-cancelled" label="Cancelled" value={stats.cancelled_subscriptions} />
        <Stat testId="analytics-content" label="Content Created" value={stats.content_created} />
        <Stat testId="analytics-revisions" label="Revision Requests" value={stats.revision_requests} />
        <Stat testId="analytics-revision-rate" label="Revision Rate" value={stats.revision_rate} hint="revisions per content item" />
        <Stat testId="analytics-tickets" label="Open Tickets" value={stats.open_tickets} />
      </div>

      <Card testId="analytics-packages">
        <p className="font-display text-lg font-semibold text-slate-900">Customers by package</p>
        {Object.keys(stats.customers_by_package || {}).length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">No active subscriptions yet.</p>
        ) : (
          <div className="mt-4 space-y-3">
            {Object.entries(stats.customers_by_package).map(([k, v]) => (
              <div key={k}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold capitalize text-slate-800">{k}</span>
                  <span className="text-slate-500">{v}</span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-200">
                  <div
                    className="h-full bg-[#F95738]"
                    style={{ width: `${(v / Math.max(1, stats.active_customers)) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card testId="analytics-industries">
        <p className="font-display text-lg font-semibold text-slate-900">Most common industries</p>
        {Object.keys(stats.industries || {}).length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">No industry data yet.</p>
        ) : (
          <div className="mt-4 flex flex-wrap gap-2">
            {Object.entries(stats.industries).map(([k, v]) => (
              <span key={k} className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700">
                {k}: {v}
              </span>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
